import { useDisclosure, UseDisclosureProps } from '@chakra-ui/react';
import { useCallback, useEffect, useRef } from 'react';

export type UseDisclosureDelayedProps = UseDisclosureProps & {
  /** Delay in ms before `onCloseDelayed` actually closes. */
  closeDelay?: number;
};

/**
 * Extends Chakra's `useDisclosure` with an `onCloseDelayed` that waits
 * `closeDelay` ms before closing. `onOpen`/`onClose`/`onToggle` cancel any
 * pending delayed close, so hovering back in before the delay elapses keeps
 * the disclosure open (e.g. moving the pointer from a trigger into a tooltip).
 */
export const useDisclosureDelayed = (props: UseDisclosureDelayedProps = {}) => {
  const { closeDelay = 500, ...disclosureProps } = props;
  const disclosure = useDisclosure(disclosureProps);
  const { onOpen: baseOnOpen, onClose: baseOnClose, onToggle: baseOnToggle } = disclosure;

  const closeHandle = useRef<ReturnType<typeof setTimeout>>();
  const clearPendingClose = useCallback(() => clearTimeout(closeHandle.current), []);

  const onOpen = useCallback(() => {
    clearPendingClose();
    baseOnOpen();
  }, [clearPendingClose, baseOnOpen]);

  const onClose = useCallback(() => {
    clearPendingClose();
    baseOnClose();
  }, [clearPendingClose, baseOnClose]);

  const onToggle = useCallback(() => {
    clearPendingClose();
    baseOnToggle();
  }, [clearPendingClose, baseOnToggle]);

  const onCloseDelayed = useCallback(() => {
    clearPendingClose();
    closeHandle.current = setTimeout(baseOnClose, closeDelay);
  }, [clearPendingClose, baseOnClose, closeDelay]);

  // Clear any pending close on unmount
  useEffect(() => clearPendingClose, [clearPendingClose]);

  return { ...disclosure, onOpen, onClose, onToggle, onCloseDelayed };
};
